// MD3 component overrides for MUI v5
// Shape: rounded corners, pill buttons, tonal surfaces

export const components = {
  // ── Global baseline ─────────────────────────────────────────────────
  MuiCssBaseline: {
    styleOverrides: {
      '*': { boxSizing: 'border-box' },
      html: {
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        height: '100%',
        width: '100%'
      },
      body: { height: '100%' },
      a: { textDecoration: 'none', color: 'inherit' },
      '#root': { height: '100%' },
      '#nprogress .bar': { zIndex: '2000 !important' }
    }
  },

  // ── Button — MD3 pill shape ──────────────────────────────────────────
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0.006em',
        textTransform: 'none',
        borderRadius: '20px',
        padding: '8px 20px',
        minHeight: 40
      },
      contained: {
        boxShadow: 'none',
        '&:hover': { boxShadow: '0px 1px 3px rgba(0,0,0,0.12)' }
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': { borderWidth: '1.5px' }
      },
      sizeLarge: { padding: '10px 24px', fontSize: '0.9375rem' },
      sizeSmall: { padding: '5px 14px', fontSize: '0.8125rem', borderRadius: '16px' }
    }
  },

  // ── IconButton ────────────────────────────────────────────────────────
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: '50%',
        padding: 8
      }
    }
  },

  // ── Card — MD3 Elevated Card ──────────────────────────────────────────
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
      }
    }
  },

  MuiCardContent: {
    styleOverrides: {
      root: { padding: '20px 24px', '&:last-child': { paddingBottom: 20 } }
    }
  },

  // ── Paper ─────────────────────────────────────────────────────────────
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: 'none' },
      rounded: { borderRadius: '12px' },
      elevation1: { boxShadow: '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)' },
      elevation2: { boxShadow: '0px 2px 4px rgba(0,0,0,0.07), 0px 1px 3px rgba(0,0,0,0.06)' },
      elevation3: { boxShadow: '0px 4px 6px rgba(0,0,0,0.07), 0px 2px 4px rgba(0,0,0,0.06)' },
      elevation4: { boxShadow: '0px 5px 8px rgba(0,0,0,0.06), 0px 3px 6px rgba(0,0,0,0.05)' }
    }
  },

  // ── TextField — MD3 Outlined ──────────────────────────────────────────
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.5px' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderWidth: '1.5px' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' }
      }
    }
  },

  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: '0.875rem' }
    }
  },

  // ── Chip — MD3 style ──────────────────────────────────────────────────
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        height: 28
      },
      sizeSmall: { height: 22, fontSize: '0.75rem', borderRadius: '6px' }
    }
  },

  // ── Table ─────────────────────────────────────────────────────────────
  MuiTable: {
    styleOverrides: { root: { tableLayout: 'auto' } }
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        padding: '10px 12px',
        borderBottom: '2px solid rgba(0,0,0,0.08)'
      },
      root: {
        fontSize: '0.875rem',
        padding: '10px 12px',
        borderBottom: '1px solid rgba(0,0,0,0.06)'
      }
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child td': { borderBottom: 0 },
        '&.MuiTableRow-hover:hover': { backgroundColor: 'rgba(27, 115, 64, 0.04)' }
      }
    }
  },

  // ── Tooltip ───────────────────────────────────────────────────────────
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: {
      tooltip: {
        fontSize: '0.75rem',
        borderRadius: '8px',
        padding: '6px 12px'
      }
    }
  },

  // ── MenuItem ──────────────────────────────────────────────────────────
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        borderRadius: '8px',
        margin: '2px 6px',
        padding: '8px 10px'
      }
    }
  },

  // ── Menu ──────────────────────────────────────────────────────────────
  MuiMenu: {
    styleOverrides: {
      paper: { borderRadius: '12px', padding: '4px 0' },
      list: { padding: '4px 0' }
    }
  },

  // ── Dialog ────────────────────────────────────────────────────────────
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: '20px' }
    }
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { fontSize: '1.25rem', fontWeight: 600, padding: '24px 24px 12px' }
    }
  },
  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '8px 24px' }
    }
  },
  MuiDialogActions: {
    styleOverrides: {
      root: { padding: '16px 24px 20px', gap: 8 }
    }
  },

  // ── AppBar ────────────────────────────────────────────────────────────
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.08)'
      }
    }
  },

  // ── Drawer ────────────────────────────────────────────────────────────
  MuiDrawer: {
    styleOverrides: {
      paper: { backgroundImage: 'none', border: 0 }
    }
  },

  // ── ListItemButton ────────────────────────────────────────────────────
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: '28px',
        margin: '2px 8px',
        padding: '10px 16px',
        '&.Mui-selected': {
          backgroundColor: 'rgba(27, 115, 64, 0.12)',
          '&:hover': { backgroundColor: 'rgba(27, 115, 64, 0.16)' }
        }
      }
    }
  },

  // ── Tab ───────────────────────────────────────────────────────────────
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        minHeight: 48
      }
    }
  },

  // ── Accordion ─────────────────────────────────────────────────────────
  MuiAccordion: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: '12px !important',
        border: '1px solid rgba(0,0,0,0.08)',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '8px 0' }
      }
    }
  },

  // ── Skeleton ──────────────────────────────────────────────────────────
  MuiSkeleton: {
    defaultProps: { animation: 'wave' },
    styleOverrides: { root: { borderRadius: '6px' } }
  },

  // ── Select ────────────────────────────────────────────────────────────
  MuiSelect: {
    styleOverrides: {
      select: { borderRadius: '4px' }
    }
  },

  // ── Fab ───────────────────────────────────────────────────────────────
  MuiFab: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        boxShadow: '0px 4px 6px rgba(0,0,0,0.07), 0px 2px 4px rgba(0,0,0,0.06)'
      }
    }
  },

  // ── TablePagination ────────────────────────────────────────────────────
  MuiTablePagination: {
    styleOverrides: {
      root: { fontSize: '0.875rem', borderTop: '1px solid rgba(0,0,0,0.06)' },
      selectLabel: { fontSize: '0.875rem' },
      displayedRows: { fontSize: '0.875rem' }
    }
  }
};
